import FaiTypography from "MuiComponents/FaiTypography";
import FaiBox from "MuiComponents/FaiBox";
import { Card, Grid } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import FaiButton from "MuiComponents/FaiButton";
import FormField from "components/FormField";
import { useState, useCallback } from "react";
import Axios from "services/api/config";
import { createMasterCategory } from "services/api/routes/Admin";
import { message } from "antd";
import FaiDropzone from "MuiComponents/FaiDropzone";

interface InitialObject {
	categoryName: string;
	description: string;
	subcategoryName: string;
}

export default function AddCategory({ onCreated = () => {} }) {
	const [initialObject] = useState<InitialObject>({
		categoryName: "",
		description: "",
		subcategoryName: "",
	});
	const [creating, setCreating] = useState(false);

	const [categoryImage, setCategoryImage] = useState<File | any | null>(null);

	// Memoize the options objects to prevent recreation on each render
	const categoryDropzoneOptions = useCallback(
		() => ({
			addRemoveLinks: true,
			uploadMultiple: false,
			// maxFiles: 1,
			acceptedFiles: "image/*",
			autoProcessQueue: false,
			thumbnailWidth: 150,
			thumbnailHeight: 150,
			previewsContainer: ".dropzone",
			init: function () {
				this.on("addedfile", function (file) {
					console.log("added ", file);
					setCategoryImage(file);
				});
				this.on("removedfile", function () {
					console.log("removed ");
					setCategoryImage(null);
				});
				this.on("thumbnail", function (file, dataUrl) {
					console.log("Thumbnail generated", file);
					file.dataURL = dataUrl; // Store the dataUrl in the file object
					setCategoryImage(file);
					// The dataUrl contains the data URL of the thumbnail
				});
			},
		}),
		[]
	);

	// const subcategoryDropzoneOptions = useCallback(
	// 	() => ({
	// 		addRemoveLinks: true,
	// 		uploadMultiple: false,
	// 		maxFiles: 1,
	// 		acceptedFiles: "image/*",
	// 		autoProcessQueue: false,
	// 		thumbnailWidth: 150,
	// 		thumbnailHeight: 150,
	// 		previewsContainer: ".dropzone",
	// 		init: function () {
	// 			this.on("addedfile", function (file) {
	// 				console.log("added sub", file);
	// 				setSubcategoryImage(file);
	// 			});
	// 			this.on("removedfile", function () {
	// 				console.log("removed sub");
	// 				setSubcategoryImage(null);
	// 			});
	// 			this.on("thumbnail", function (file, dataUrl) {
	// 				console.log("Thumbnail generated");
	// 				file.dataURL = dataUrl; // Store the dataUrl in the file object
	// 				setSubcategoryImage(file);
	// 				// The dataUrl contains the data URL of the thumbnail
	// 			});
	// 		},
	// 	}),
	// 	[]
	// );

	const handleCreate = async (values: InitialObject): Promise<void> => {
		try {
			// Create master category first
			if (!values.categoryName) {
				message.error("Category name is required");
				return;
			}
			if (values.categoryName) {
				const formData = new FormData();
				formData.append("name", values.categoryName);
				formData.append("description", values.description || "");
				if (categoryImage) {
					formData.append("media", categoryImage);
				} else {
					message.error("Category image is required");
					return;
				}
				setCreating(true);

				const { data: masterCategoryData } = await Axios({
					...createMasterCategory,
					data: formData,
					headers: {
						"Content-Type": "multipart/form-data",
					},
				});

				// If subcategory is provided, create it with the master category ID
				// if (values.subcategoryName && masterCategoryData?.data?._id) {
				// 	const subCategoryFormData = new FormData();
				// 	subCategoryFormData.append("name", values.subcategoryName);
				// 	subCategoryFormData.append(
				// 		"masterCategoryId",
				// 		masterCategoryData.data._id
				// 	);

				// 	if (subcategoryImage) {
				// 		subCategoryFormData.append("media", subcategoryImage);
				// 	} else {
				// 		message.error("Subcategory image is required");
				// 		throw new Error("Subcategory image is required");
				// 	}

				// 	await Axios({
				// 		...createSubCategory,
				// 		data: subCategoryFormData,
				// 		headers: {
				// 			"Content-Type": "multipart/form-data",
				// 		},
				// 	});
				// }
				onCreated();
				message.success("Category created successfully");
				// Reset form and state

				setCategoryImage(null);
				// setSubcategoryImage(null);
			}
		} catch (error: any) {
			message.error(error?.errorMessage || "Failed to create categories");
			throw error; // Re-throw the error to be caught by the promise chain
		} finally {
			setCreating(false);
		}
	};

	// const handleUploadExcelSheet = async (file: File) => {
	// 	try {
	// 		const formData = new FormData();
	// 		formData.append("file", file);

	// 		await Axios({
	// 			...uploadMasterCategoriesExcel,
	// 			data: formData,
	// 			headers: {
	// 				"Content-Type": "multipart/form-data",
	// 			},
	// 		});

	// 		message.success("Excel sheet uploaded successfully");
	// 	} catch (error: any) {
	// 		message.error(error?.errorMessage || "Failed to upload Excel sheet");
	// 	}
	// };

	return (
		<Card className="my-6 p-3 md:p-6">
			<FaiBox className={"inventory"}>
				<FaiBox>
					<FaiTypography
						variant="h4"
						className="!text-xl !font-semibold"
						mb={2}
					>
						Add New Category
					</FaiTypography>
				</FaiBox>
			</FaiBox>
			<FaiBox>
				<Formik
					initialValues={initialObject}
					enableReinitialize={true}
					validateOnBlur={true}
					validateOnChange={true}
					onSubmit={(values, actions) => {
						handleCreate(values)
							.then(() => {
								// Reset form after successful submission
								actions.resetForm();
								actions.setSubmitting(false);
							})
							.catch(() => {
								actions.setSubmitting(false);
							});
					}}
				>
					{(props: FormikProps<InitialObject>) => {
						const { handleSubmit, handleChange, touched, values, errors } =
							props;

						return (
							<Form onSubmit={handleSubmit} className="space-y-2">
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<FaiBox xs={12} sm={6}>
											<FormField
												label={"Category Name"}
												name={"categoryName"}
												placeholder={"Enter Category"}
												value={values.categoryName}
												onChange={handleChange}
												error={
													touched.categoryName && Boolean(errors.categoryName)
												}
												helperText={touched.categoryName && errors.categoryName}
												isRequired
											/>
										</FaiBox>
										<FaiBox xs={12} sm={6} mt={2}>
											<FormField
												label={"Description"}
												name={"description"}
												placeholder={"Enter Description"}
												value={values.description}
												onChange={handleChange}
												error={touched.description && Boolean(errors.description)}
												helperText={touched.description && errors.description}
												multiline
												rows={3}
											/>
										</FaiBox>
										<FaiBox mt={2} xs={12} sm={6}>
											<FaiBox mb={1}>
												<FaiTypography
													component="label"
													variant="caption"
													className="!text-base !font-medium"
													isRequired
												>
													Category Image
												</FaiTypography>
											</FaiBox>
											{categoryImage ? (
												<FaiBox
													position="relative"
													className="border rounded-lg h-40"
												>
													<div
														style={{
															position: "absolute",
															top: "-10px",
															right: "-10px",
															cursor: "pointer",
															background: "var(--theme-color)",
															color: "white",
															borderRadius: "50%",
															width: "24px",
															height: "24px",
															display: "flex",
															justifyContent: "center",
															alignItems: "center",
															boxShadow: "0 0 5px rgba(0,0,0,0.2)",
														}}
														onClick={() => setCategoryImage(null)}
													>
														<span
															style={{ fontSize: "16px", fontWeight: "bold" }}
														>
															&times;
														</span>
													</div>
													<img
														src={categoryImage.dataURL}
														alt="Category Preview"
														className="mx-auto max-h-36 mt-2"
														style={{
															objectFit: "contain",
														}}
													/>
												</FaiBox>
											) : (
												<FaiDropzone
													options={categoryDropzoneOptions()}
													note="Upload category image (required)"
												/>
											)}
										</FaiBox>
									</Grid>
									{/* <Grid item xs={12} sm={6}>
										<FaiBox xs={12}>
											<FormField
												label={"Sub-Category Name"}
												name={"subcategoryName"}
												placeholder={"Enter Sub-Category"}
												value={values.subcategoryName}
												onChange={handleChange}
												error={
													touched.subcategoryName &&
													Boolean(errors.subcategoryName)
												}
												helperText={
													touched.subcategoryName && errors.subcategoryName
												}
												isRequired
											/>
										</FaiBox>
										<FaiBox mt={2}>
											<FaiBox mb={1}>
												<FaiTypography
													component="label"
													variant="caption"
													className="!text-base !font-medium"
													isRequired
												>
													Sub-Category Image
												</FaiTypography>
											</FaiBox>
											{subcategoryImage ? (
												<FaiBox
													position="relative"
													className="border rounded-lg h-40"
												>
													<div
														style={{
															position: "absolute",
															top: "-10px",
															right: "-10px",
															cursor: "pointer",
															background: "var(--theme-color)",
															color: "white",
															borderRadius: "50%",
															width: "24px",
															height: "24px",
															display: "flex",
															justifyContent: "center",
															alignItems: "center",
															boxShadow: "0 0 5px rgba(0,0,0,0.2)",
														}}
														onClick={() => setSubcategoryImage(null)}
													>
														<span
															style={{ fontSize: "16px", fontWeight: "bold" }}
														>
															&times;
														</span>
													</div>
													<img
														src={subcategoryImage.dataURL}
														alt="Sub-Category Preview"
														className="mx-auto max-h-40 mt-2"
														style={{
															objectFit: "contain",
														}}
													/>
												</FaiBox>
											) : (
												<FaiDropzone
													options={subcategoryDropzoneOptions()}
													note="Upload sub-category image (required)"
												/>
											)}
										</FaiBox>
									</Grid> */}
									<Grid className="flex items-end" item xs={12} mt={3}>
										<FaiButton
											color="primary"
											variant={"contained"}
											fullWidth
											type="submit"
											disabled={creating}
										>
											{creating ? "Creating..." : "Create"}
										</FaiButton>
									</Grid>
								</Grid>
								{/* {excelsheetUpload && (
									<Grid container spacing={2}>
										<Grid className="mt-4" item xs={12}>
											<FaiButton
												color="secondary"
												variant={"contained"}
												fullWidth
												component="label"
											>
												Upload excel sheet
												<input
													type="file"
													hidden
													accept=".xlsx,.xls"
													onChange={(e) => {
														const file = e.target.files?.[0];
														if (file) {
															handleUploadExcelSheet(file);
														}
													}}
												/>
											</FaiButton>
										</Grid>
									</Grid>
								)} */}
							</Form>
						);
					}}
				</Formik>
			</FaiBox>
		</Card>
	);
}
